import React, { useState } from "react";
import ReactMarkdown from "react-markdown";
import {
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Tooltip, CircularProgress,
} from "@mui/material";
import { Download, FileText, FileSpreadsheet, Image as ImageIcon, AlertTriangle } from "lucide-react";
import { parseTelegram } from "../../utils/telegramMarkdown";
import { downloadChatFile } from "../../api/chat_api";

/* ── one text block from the bot ──────────────────────────────────────── */

const mdComponents = {
    // Each line is its own paragraph so the bot's single newlines survive.
    p: ({ children }) => <span>{children}</span>,
    a: ({ href, children }) => (
        <a href={href} target="_blank" rel="noopener noreferrer" style={{ color: "var(--color-primary)" }}>
            {children}
        </a>
    ),
    code: ({ children }) => (
        <code
            style={{
                fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
                fontSize: "0.92em",
                padding: "1px 5px",
                borderRadius: 4,
                backgroundColor: "var(--color-bg-tertiary)",
                border: "1px solid var(--color-border-primary)",
            }}
        >
            {children}
        </code>
    ),
};

const MdBlock = ({ lines }) => (
    <div className="fa-chat-md">
        {lines.map((line, k) =>
            line === "" ? (
                <div key={k} style={{ height: 8 }} />
            ) : (
                <div key={k} style={{ lineHeight: 1.55, wordBreak: "break-word" }}>
                    <ReactMarkdown components={mdComponents} allowedElements={["p", "strong", "em", "code", "a"]} unwrapDisallowed>
                        {line}
                    </ReactMarkdown>
                </div>
            )
        )}
    </div>
);

/* ── a table the bot drew in monospace, now a real one ────────────────── */

const gradeColor = (v) => {
    const g = String(v || "").trim();
    if (/^A[+-]?$/.test(g)) return "var(--color-success)";
    if (/^B[+-]?$/.test(g)) return "#66BB6A";
    if (/^C[+-]?$/.test(g)) return "#F59E0B";
    if (/^[DEF][+-]?$/.test(g)) return "var(--color-error)";
    return null;
};

const cellSx = (align, dense) => ({
    py: dense ? 0.4 : 0.75,
    px: dense ? 1 : 1.5,
    fontSize: dense ? 12 : 13.5,
    textAlign: align,
    whiteSpace: "nowrap",
    borderColor: "var(--color-border-primary)",
    color: "var(--color-text-primary)",
    fontVariantNumeric: "tabular-nums",
});

const TableBlock = ({ header, rows, align, dense }) => (
    <TableContainer
        sx={{
            my: 0.75,
            borderRadius: 1.5,
            border: "1px solid var(--color-border-primary)",
            backgroundColor: "var(--color-surface)",
            maxWidth: "100%",
            overflowX: "auto",
        }}
    >
        <Table size="small" aria-label="bot table">
            <TableHead>
                <TableRow sx={{ backgroundColor: "var(--color-bg-tertiary)" }}>
                    {header.map((h, k) => (
                        <TableCell key={k} sx={{ ...cellSx(align[k], dense), fontWeight: 700, color: "var(--color-text-secondary)", fontSize: dense ? 11.5 : 12.5, textTransform: "none" }}>
                            {h}
                        </TableCell>
                    ))}
                </TableRow>
            </TableHead>
            <TableBody>
                {rows.map((r, i) => (
                    <TableRow key={i} hover sx={{ "&:last-child td": { borderBottom: 0 } }}>
                        {r.map((c, k) => {
                            const color = gradeColor(c);
                            return (
                                <TableCell key={k} sx={{ ...cellSx(align[k], dense), ...(k === 0 ? { fontWeight: 600 } : {}), ...(color ? { color, fontWeight: 700 } : {}) }}>
                                    {c}
                                </TableCell>
                            );
                        })}
                    </TableRow>
                ))}
            </TableBody>
        </Table>
    </TableContainer>
);

const CodeBlock = ({ text }) => (
    <pre
        style={{
            margin: "6px 0",
            padding: "8px 10px",
            borderRadius: 6,
            fontSize: 12,
            lineHeight: 1.45,
            overflowX: "auto",
            backgroundColor: "var(--color-bg-tertiary)",
            border: "1px solid var(--color-border-primary)",
            color: "var(--color-text-primary)",
            fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
        }}
    >
        {text}
    </pre>
);

/* ── a report the bot sent as a document ──────────────────────────────── */

const fmtSize = (n) => (n > 1_048_576 ? `${(n / 1_048_576).toFixed(1)} MB` : n > 1024 ? `${Math.round(n / 1024)} KB` : `${n} B`);

const FileChip = ({ file, onAuthExpired }) => {
    const [state, setState] = useState("idle"); // idle | busy | error
    const [err, setErr] = useState("");
    const Icon = /sheet|excel|xlsx/i.test(file.mime + file.name) ? FileSpreadsheet : file.kind === "photo" || /^image\//.test(file.mime) ? ImageIcon : FileText;

    const go = async () => {
        setState("busy");
        const res = await downloadChatFile(file);
        if (res.authExpired) { onAuthExpired?.(); return; }
        if (!res.success) { setErr(res.error || "Download failed"); setState("error"); return; }
        setState("idle");
    };

    return (
        <Tooltip title={state === "error" ? err : `Download ${file.name}`} arrow>
            <button
                type="button"
                onClick={go}
                disabled={state === "busy"}
                style={{
                    display: "inline-flex", alignItems: "center", gap: 8, margin: "6px 6px 0 0",
                    padding: "8px 12px", borderRadius: 8, cursor: "pointer", textAlign: "left",
                    border: `1px solid ${state === "error" ? "var(--color-error)" : "var(--color-border-primary)"}`,
                    backgroundColor: "var(--color-surface)", color: "var(--color-text-primary)", fontSize: 13,
                }}
            >
                {state === "busy" ? <CircularProgress size={16} /> : state === "error" ? <AlertTriangle size={16} color="var(--color-error)" /> : <Icon size={16} color="var(--color-primary)" />}
                <span style={{ display: "flex", flexDirection: "column", lineHeight: 1.25 }}>
                    <span style={{ fontWeight: 600 }}>{file.caption || file.name}</span>
                    <span style={{ fontSize: 11, color: "var(--color-text-muted)" }}>{file.name} · {fmtSize(file.size || 0)}</span>
                </span>
                <Download size={14} style={{ marginLeft: 4, opacity: 0.7 }} />
            </button>
        </Tooltip>
    );
};

/* ── the message ──────────────────────────────────────────────────────── */

const bubbleStyle = (role) => ({
    maxWidth: role === "user" ? "82%" : "100%",
    padding: role === "system" ? "6px 10px" : "10px 14px",
    borderRadius: 12,
    fontSize: 14,
    ...(role === "user"
        ? { backgroundColor: "var(--color-primary)", color: "#fff", borderBottomRightRadius: 4, whiteSpace: "pre-wrap", wordBreak: "break-word" }
        : role === "system"
            ? { backgroundColor: "transparent", color: "var(--color-text-muted)", fontSize: 12, fontStyle: "italic", textAlign: "center", maxWidth: "100%" }
            : { backgroundColor: "var(--color-surface)", color: "var(--color-text-primary)", border: "1px solid var(--color-border-primary)", borderBottomLeftRadius: 4 }),
});

/**
 * One turn in the conversation.
 *   { role: "user",   text }
 *   { role: "bot",    replies: [...], sent?, busy? }
 *   { role: "system", text }
 */
const ChatMessage = ({ message, dense = false, onAuthExpired }) => {
    const { role } = message;

    if (role === "user") {
        return (
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
                <div style={bubbleStyle("user")}>{message.text}</div>
            </div>
        );
    }
    if (role === "system") {
        return (
            <div style={{ display: "flex", justifyContent: "center" }}>
                <div style={bubbleStyle("system")}>{message.text}</div>
            </div>
        );
    }

    const replies = message.replies || [];
    return (
        <div style={{ display: "flex", justifyContent: "flex-start", flexDirection: "column", gap: 6 }}>
            {message.sent && message.sent !== message.typed && (
                <div style={{ fontSize: 11, color: "var(--color-text-muted)", paddingLeft: 4 }}>ran as <code>{message.sent}</code></div>
            )}
            {replies.length === 0 && (
                <div style={bubbleStyle("bot")}><em style={{ color: "var(--color-text-muted)" }}>The bot sent nothing back for that.</em></div>
            )}
            {replies.map((r, i) =>
                r.type === "file" ? (
                    <div key={i}><FileChip file={r} onAuthExpired={onAuthExpired} /></div>
                ) : (
                    <div key={i} style={bubbleStyle("bot")}>
                        {r.markdown === false ? (
                            <div style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{r.text}</div>
                        ) : (
                            parseTelegram(r.text).map((b, k) =>
                                b.type === "table" ? <TableBlock key={k} {...b} dense={dense} />
                                    : b.type === "code" ? <CodeBlock key={k} text={b.text} />
                                        : <MdBlock key={k} lines={b.lines} />
                            )
                        )}
                    </div>
                )
            )}
        </div>
    );
};

export default ChatMessage;
